import { useCallback, useMemo } from 'react'
import { useCurrencyAndLanguageProvider } from 'src/context/CurrencyAndLanguageProvider'
import {
  Currency,
  DealType,
  ICheckout,
  IConfiguration,
  IConfigurationInput,
  ILocation,
  Maybe
} from 'src/types/graphql'
import {
  getEffectiveDealTypeFromPrice,
  isBrokerVariant
} from 'src/utils/pricing'

const USD_TO_MXN = 20

export default function usePrice(
  location: Maybe<ILocation> | undefined,
  config: Maybe<IConfiguration> | undefined
) {
  const { getPrice } = useGetPrice()
  const fprice = getPrice(location, config)
  return fprice
}

export function useGetPrice() {
  const { currentCurrency } = useCurrencyAndLanguageProvider()

  const convertPrice = useCallback(
    (location: ILocation, price: number) =>
      currentCurrency === Currency.Usd
        ? location.currentCurrency !== Currency.Usd
          ? price / USD_TO_MXN
          : price
        : price,
    [currentCurrency]
  )

  /**
   * Broker-mode boolean for a configuration. Thin wrapper around the
   * canonical helpers in `src/utils/pricing.ts` — single source of truth.
   *
   *   - `inEdit=false` (default): respect the stored `dealType`. Used by
   *     listing-page surfaces that should render exactly what's saved.
   *   - `inEdit=true`: ignore stored `dealType` and recompute from the
   *     current input price. Used in EditConfig / EditListing / AdminEdit
   *     / AddPostFlow where the user is mid-edit and the stored value is
   *     stale until save.
   */
  const getIsBrokerMode = useCallback(
    (
      config: Maybe<IConfiguration | IConfigurationInput> | undefined,
      inEdit?: boolean
    ) => {
      if (!config) return false
      if (inEdit) {
        return getEffectiveDealTypeFromPrice(config.price) === DealType.Broker
      }
      return isBrokerVariant(config)
    },
    []
  )

  const getPrice = useCallback(
    (
      location: Maybe<ILocation> | undefined,
      config: Maybe<IConfiguration | IConfigurationInput> | undefined
    ) => {
      if (!location || !config) return 0
      const price = config.price ?? 0
      return convertPrice(location, price)
    },
    [convertPrice]
  )

  const getPriceCheckout = useCallback(
    (
      location: Maybe<ILocation> | undefined,
      checkout: Maybe<ICheckout> | undefined
    ) => {
      if (!location || !checkout) return 0
      const { price } = checkout ?? {}
      return convertPrice(location, price ?? 0)
    },
    [convertPrice]
  )

  const value = useMemo(() => {
    return { getPrice, getPriceCheckout, getIsBrokerMode }
  }, [getPrice, getPriceCheckout, getIsBrokerMode])

  return value
}
